// hooks for SpotlightApi

import { useCallback } from 'react';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { GENERAL_API_STALE } from 'config/query-config';
import { SpotlightApi } from '../api/spotlight/api';
import {
  CustomerPin,
  PinResourceType,
  PinScopeType,
} from '../api/spotlight/types';

export const useGetSpotlights = (
  args:
    | {
        scopeId: number | undefined;
        scopeType: string | undefined;
        resourceType: string;
      }
    | undefined,
) => {
  const { scopeId, scopeType, resourceType } = args || {};

  return useQuery(
    [
      'spotlight',
      'getPinnedResourcesByParentAndAccountAndResourceType',
      scopeId,
      scopeType,
      resourceType,
    ],
    () => {
      if (!args) {
        return Promise.resolve([]);
      }

      return scopeId && scopeType && resourceType
        ? SpotlightApi.getPinsByScopeAndResource(
            scopeId,
            scopeType,
            resourceType,
          )
        : Promise.resolve([]);
    },
    {
      refetchOnMount: true,
      staleTime: GENERAL_API_STALE,
      enabled: !!scopeId && !!scopeType,
    },
  );
};

export const useGetPinsByResourceAndScope = ({
  scopeId,
  scopeType,
  resourceId,
  resourceType,
}: {
  resourceId: number;
  resourceType: string;
  scopeId: number;
  scopeType: string;
}) => {
  return useQuery(
    [
      'spotlight',
      'getMappingByResourceAndParentAndAccount',
      resourceId,
      resourceType,
      scopeId,
      scopeType,
    ],
    () =>
      SpotlightApi.getPinByResourceAndScope(
        resourceId,
        resourceType,
        scopeId,
        scopeType,
      ),
    { refetchOnMount: true, staleTime: GENERAL_API_STALE },
  );
};

// upsertPinnedOnResourceAndParentAndAccount
export const useAddPinsOnResourceAndScope = () => {
  const queryClient = useQueryClient();

  return useMutation(
    async ({
      mapping,
      scopeId,
      scopeType,
      resourceId,
      resourceType,
    }: {
      resourceId: number;
      resourceType: string;
      scopeId: number;
      scopeType: string;
      mapping: Partial<CustomerPin>;
    }) => {
      return SpotlightApi.addPinOnResourceWithScope(
        resourceId,
        resourceType,
        scopeId,
        scopeType,
        mapping,
      );
    },

    {
      onSuccess: (_data, { scopeId, resourceId, scopeType, resourceType }) => {
        queryClient.invalidateQueries([
          'spotlight',
          'getMappingByResourceAndParentAndAccount',
          resourceId,
          resourceType,
          scopeId,
          scopeType,
        ]);
        queryClient.invalidateQueries([
          'spotlight',
          'getPinnedResourcesByParentAndAccountAndResourceType',
          scopeId,
          scopeType,
          resourceType,
        ]);
      },
    },
  );
};

// deleteMapping parent and resource
export const useDeleteMappingParentAndResource = () => {
  const queryClient = useQueryClient();

  return useMutation(
    async ({
      resourceId,
      resourceType,
      scopeId,
      scopeType,
    }: {
      resourceId: number;
      resourceType: string;
      scopeId: number;
      scopeType: string;
    }) => {
      return SpotlightApi.deletePinOnResourceWithScope(
        resourceId,
        resourceType,
        scopeId,
        scopeType,
      );
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries('spotlight');
      },
    },
  );
};

export const useToggleSpotlight = ({
  resourceType,
  scopeId,
  scopeType,
}: {
  scopeId: number | undefined;
  scopeType: PinScopeType;
  resourceType: PinResourceType;
}) => {
  const { data: spotlights = [], refetch } = useGetSpotlights({
    scopeId,
    scopeType,
    resourceType,
  });
  const { mutateAsync: addSpotlight } = useAddPinsOnResourceAndScope();
  const { mutateAsync: deleteSpotlight } = useDeleteMappingParentAndResource();

  const onSpotlightToggle = useCallback(
    (resourceId: number, scopeIdArg?: number, caption = '') => {
      if (!resourceId) {
        return;
      }
      const spotlight = spotlights.find((s) => s.resourceId === resourceId);

      const _scopeId = scopeIdArg || scopeId;

      if (!_scopeId) {
        return;
      }

      const deleteFn = () => {
        deleteSpotlight({
          resourceId,
          resourceType,
          scopeId: _scopeId,
          scopeType,
        }).then(() => {
          refetch();
        });
      };

      const addFn = () => {
        addSpotlight({
          resourceId,
          resourceType,
          scopeId: _scopeId,
          scopeType,
          mapping: {
            caption,
            resourceType,
            resourceId,
            scopeType,
            scopeId,
          },
        }).then(() => {
          refetch();
        });
      };

      if (spotlight) {
        if (spotlight?.deleted) {
          addFn();
        } else {
          deleteFn();
        }
      } else {
        addFn();
      }
    },
    [
      addSpotlight,
      deleteSpotlight,
      resourceType,
      scopeId,
      scopeType,
      spotlights,
      refetch,
    ],
  );

  return {
    spotlights,
    onSpotlightToggle,
    refetch,
  };
};
